import { useId, useState } from 'react'
import type { FormEvent } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import { LanguageSwitcher } from '../../app/LanguageSwitcher'
import { ApiError } from '../../lib/api'
import logoUrl from '../../assets/truckers-corner-logo.png'
import { useAuth } from './context'

export function LoginPage() {
  const { t } = useTranslation()
  const { status, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  // Shown on request (TRKC-97): a long password typed blind on a phone is how a sign-in
  // fails three times and locks the account for fifteen minutes.
  const [showPassword, setShowPassword] = useState(false)
  // What a screen reader is told after the button is pressed — its own new name is not
  // announced while it keeps the focus. Empty until it is pressed.
  const [visibility, setVisibility] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const passwordId = useId()

  // Where the guard sent us from, so signing in lands back there — query and
  // fragment included: /loads?status=open#today must survive the round trip.
  const origin = (
    location.state as {
      from?: { pathname?: string; search?: string; hash?: string }
    } | null
  )?.from
  // "/" is the public landing, so a direct sign-in lands on the dashboard instead.
  const from = origin
    ? `${origin.pathname ?? '/dashboard'}${origin.search ?? ''}${origin.hash ?? ''}`
    : '/dashboard'

  // Hold the form until the startup refresh resolves. A sign-in racing that refresh
  // could have its brand-new cookie wiped by the older response's Set-Cookie
  // deletion — the generation guard protects React state, not browser cookies.
  if (status === 'initializing') {
    return <p className="session-check">{t('auth.checkingSession')}</p>
  }

  if (status === 'authenticated') {
    return <Navigate to={from} replace />
  }

  function togglePassword() {
    const next = !showPassword
    setShowPassword(next)
    setVisibility(next ? t('auth.passwordShown') : t('auth.passwordHidden'))
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    // Back to dots before anything leaves: a password must never sit in a text box the
    // browser could remember as form history.
    setShowPassword(false)
    setVisibility('')
    setIsSubmitting(true)
    setError(null)

    try {
      await login(email, password)
      navigate(from, { replace: true })
    } catch (caught) {
      // The API's ProblemDetails text is English-only; translating by status keeps
      // the message in the user's language.
      setError(
        caught instanceof ApiError && caught.status === 401
          ? t('auth.invalidCredentials')
          : t('auth.error'),
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="login">
      <section className="login__brand">
        {/* Full artwork, wordmark plate included (Lino, 2026-08-28 — the old art's
            plate mushed at this size; the new one is legible, so the emblem-only
            crop retired). The name lives in the pixels now, so the image IS the
            announcement, and only the tagline is typeset beneath it. */}
        <svg
          className="login__logo"
          viewBox="0 0 470 470"
          role="img"
          aria-label={t('app.name')}
        >
          <image href={logoUrl} width="470" height="470" />
        </svg>
        <span className="login__rule" aria-hidden="true" />
        <p className="login__brandtag">{t('app.tagline')}</p>
      </section>

      <section className="login__pane">
        {/* The one screen where switching languages matters most: the visitor has no
            session yet, so the header switcher does not exist for them. */}
        <div className="login__lang">
          <LanguageSwitcher />
        </div>

        <div className="login__body">
          <h1>{t('auth.title')}</h1>
          <p className="page__subtitle">{t('auth.subtitle')}</p>

          <form className="form login__form" onSubmit={(event) => void handleSubmit(event)}>
            <label className="field">
              <span>{t('auth.email')}</span>
              <input
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </label>

            {/* The button sits beside the box, outside its label: inside it, it would
                be part of how the box is named. Its words are its name, never an
                aria-label — "Password" must keep naming one thing on this screen. */}
            <div className="field">
              <label htmlFor={passwordId}>{t('auth.password')}</label>
              <div className="login__password">
                <input
                  id={passwordId}
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  // Shown as text, a phone would capitalise and correct it, and a
                  // browser's spellchecker could send it off to be checked.
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                />
                {/* One short word on screen, so the box keeps its room on a phone; the
                    whole name read aloud, from hidden text rather than an aria-label. */}
                <button
                  type="button"
                  className="button button--quiet"
                  aria-controls={passwordId}
                  onClick={togglePassword}
                >
                  <span aria-hidden="true">{showPassword ? t('auth.hide') : t('auth.show')}</span>
                  <span className="visually-hidden">
                    {showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  </span>
                </button>
              </div>
              <span className="visually-hidden" role="status">
                {visibility}
              </span>
            </div>

            <button type="submit" className="button" disabled={isSubmitting}>
              {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>

          {error !== null && (
            <p className="alert" role="alert">
              {error}
            </p>
          )}
        </div>
      </section>
    </main>
  )
}
